import { useState, useMemo } from "react";

interface PaginationConfig {
  totalItems: number;
  initialItemsPerPage?: number;
}

// Overloaded function to support both old and new interfaces
export function usePagination<T>(items: T[], itemsPerPage?: number): {
  items: T[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  resetPagination: () => void;
};

export function usePagination(config: PaginationConfig): {
  currentPage: number;
  itemsPerPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  resetPagination: () => void;
  getPaginationInfo: () => {
    startItem: number;
    endItem: number;
    totalItems: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
};

export function usePagination<T>(
  itemsOrConfig: T[] | PaginationConfig,
  itemsPerPageParam?: number
) {
  const [currentPage, setCurrentPage] = useState(1);

  // Check if it's the old interface (array) or new interface (config object)
  const isOldInterface = Array.isArray(itemsOrConfig);
  
  if (isOldInterface) {
    // Old interface implementation
    const items = itemsOrConfig as T[];
    const itemsPerPage = itemsPerPageParam || 20;
    const totalPages = Math.ceil(items.length / itemsPerPage);
    
    const paginatedItems = useMemo(() => {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return items.slice(startIndex, endIndex);
    }, [items, currentPage, itemsPerPage]);

    const goToPage = (page: number) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const nextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    const prevPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const resetPagination = () => {
      setCurrentPage(1);
    };

    return {
      items: paginatedItems,
      currentPage,
      totalPages,
      totalItems: items.length,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1,
      goToPage,
      nextPage,
      prevPage,
      resetPagination
    };
  } else {
    // New interface implementation
    const config = itemsOrConfig as PaginationConfig;
    const { totalItems, initialItemsPerPage = 10 } = config;
    const itemsPerPage = initialItemsPerPage;

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    const goToPage = (page: number) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const goToNextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    const goToPrevPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const resetPagination = () => {
      setCurrentPage(1);
    };

    const getPaginationInfo = () => {
      const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
      const endItem = Math.min(currentPage * itemsPerPage, totalItems);
      
      return {
        startItem,
        endItem,
        totalItems,
        hasNextPage: currentPage < totalPages,
        hasPrevPage: currentPage > 1
      };
    };

    return {
      currentPage,
      itemsPerPage,
      setCurrentPage,
      totalPages,
      totalItems,
      hasNextPage: currentPage < totalPages,
      hasPrevPage: currentPage > 1,
      goToPage,
      goToNextPage,
      goToPrevPage,
      resetPagination,
      getPaginationInfo
    };
  }
}